/**
 * Stage 2 — Draw the Letter.
 *
 * Flow:
 *   1. Intro phase (2.4 s): the letter "writes itself" via an SVG
 *      stroke-dashoffset animation over the dotted guide. A bouncing
 *      pencil emoji follows alongside so the child sees a worked example
 *      before they try. This teaches shape + direction without needing
 *      per-letter stroke_path_data (which is a Phase 2 upgrade).
 *   2. Drawing phase: the intro fades to a low-opacity background guide;
 *      the trace canvas becomes interactive. The child can draw MULTIPLE
 *      strokes — critical for letters with dots (ب ت ث ج ش ض …). Each
 *      stroke stays on the canvas; 2 seconds of inactivity OR tapping
 *      "أَتْمَمْتُ" commits the whole composition as one attempt. 3 clean
 *      attempts pass the stage.
 *   3. Done: Celebration component.
 *
 * "Show me again" replays the intro at any point.
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import { useLocation, useRoute } from 'preact-iso';
import { api } from '../lib/api';
import { playUrl, unlockAudio, letterNameUrl } from '../lib/audio';
import { refreshMap } from '../state/content';
import { Spinner } from '../components/Spinner';
import { Celebration } from '../components/Celebration';
import { NavButton } from '../components/NavButton';
import { TraceCanvas, scoreTrace, type TraceAttempt } from '../lib/tracing';
import type { LetterSummary } from '../state/content';
import { currentUser } from '../state/user';

const TARGET_TRACES = 3;
const INTRO_MS = 2400;
/** How long to wait after pointerup before committing a multi-stroke attempt. */
const IDLE_COMMIT_MS = 2000;

type Phase = 'intro' | 'drawing';

export function StageDraw() {
  const { route } = useLocation();
  const { params } = useRoute();
  const letterId = Number(params.id);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const traceRef = useRef<TraceCanvas | null>(null);
  /** Accumulated strokes for the current attempt. Multi-stroke letters
   *  (ب ت ث …) need this to stick between pointerup and the next stroke. */
  const strokeBuffer = useRef<TraceAttempt[]>([]);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [letter, setLetter] = useState<LetterSummary | null>(null);
  const [phase, setPhase] = useState<Phase>('intro');
  const [introKey, setIntroKey] = useState(0); // bump to replay
  const [completed, setCompleted] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [hint, setHint] = useState<string | null>(null);
  const [finalStars, setFinalStars] = useState<number | null>(null);
  const [hasStrokes, setHasStrokes] = useState(false); // enables the Done button

  useEffect(() => {
    api<{ ok: true; letter: LetterSummary }>(`/api/letters/${letterId}`).then(
      (r) => {
        setLetter(r.letter);
        unlockAudio();
        setTimeout(() => void playUrl(letterNameUrl(r.letter.id)), 300);
      },
    );
  }, [letterId]);

  // Auto-advance to drawing phase after the intro completes.
  useEffect(() => {
    if (phase !== 'intro' || !letter) return;
    const t = setTimeout(() => setPhase('drawing'), INTRO_MS + 200);
    return () => clearTimeout(t);
  }, [phase, introKey, letter]);

  // Canvas event wiring — only active during the drawing phase.
  useEffect(() => {
    if (phase !== 'drawing' || !canvasRef.current) return;
    const canvas = canvasRef.current;

    // Resize first, then wire events. fitToDisplay reads the rendered
    // bounding box, so the canvas must have its `w-full h-full` layout
    // computed at this point.
    const tc = new TraceCanvas(canvas);
    tc.fitToDisplay();
    traceRef.current = tc;

    let drawing = false;
    let activePointer = -1;

    const getXY = (clientX: number, clientY: number) => {
      const rect = canvas.getBoundingClientRect();
      return { x: clientX - rect.left, y: clientY - rect.top };
    };

    const onDown = (e: PointerEvent) => {
      e.preventDefault();
      drawing = true;
      activePointer = e.pointerId;
      // Any new stroke cancels the idle-commit timer so the child can
      // keep adding dots without having the canvas auto-submit.
      if (idleTimer.current) {
        clearTimeout(idleTimer.current);
        idleTimer.current = null;
      }
      try {
        canvas.setPointerCapture(e.pointerId);
      } catch {
        /* iOS Safari sometimes refuses capture — drawing still works */
      }
      const { x, y } = getXY(e.clientX, e.clientY);
      tc.startAt(x, y);
    };
    const onMove = (e: PointerEvent) => {
      if (!drawing || e.pointerId !== activePointer) return;
      e.preventDefault();
      const { x, y } = getXY(e.clientX, e.clientY);
      tc.moveTo(x, y);
    };
    const onUp = (e?: PointerEvent) => {
      if (!drawing) return;
      drawing = false;
      activePointer = -1;
      if (e) {
        try {
          canvas.releasePointerCapture(e.pointerId);
        } catch {
          /* may already be released */
        }
      }
      const stroke = tc.end();
      if (!stroke) return;

      // Accumulate the stroke. The canvas paint itself is additive, so
      // visually all strokes stay visible. The buffer powers the final
      // score calculation.
      strokeBuffer.current.push(stroke);
      setHasStrokes(true);

      // Start/reset the idle-commit timer. If the child adds another
      // stroke (a dot above ت for example) within IDLE_COMMIT_MS, this
      // gets cleared in onDown.
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(commitAttempt, IDLE_COMMIT_MS);
    };

    // Pointer events cover mouse + touch + pen in one API — supported
    // by iOS 13+, Android Chrome 55+, every desktop browser. Passive
    // false lets us call preventDefault to stop pull-to-refresh.
    canvas.addEventListener('pointerdown', onDown, { passive: false });
    canvas.addEventListener('pointermove', onMove, { passive: false });
    canvas.addEventListener('pointerup', onUp as EventListener);
    canvas.addEventListener('pointercancel', onUp as EventListener);
    canvas.addEventListener('pointerleave', onUp as EventListener);

    const onResize = () => tc.fitToDisplay();
    window.addEventListener('resize', onResize);

    // One more fit-to-display after layout stabilizes — iOS Safari sometimes
    // reports a stale rect on the first synchronous read after a CSS change.
    const fitAfter = setTimeout(() => tc.fitToDisplay(), 60);

    return () => {
      clearTimeout(fitAfter);
      if (idleTimer.current) {
        clearTimeout(idleTimer.current);
        idleTimer.current = null;
      }
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp as EventListener);
      canvas.removeEventListener('pointercancel', onUp as EventListener);
      canvas.removeEventListener('pointerleave', onUp as EventListener);
      window.removeEventListener('resize', onResize);
    };
  }, [phase]);

  /** Fuse the buffered strokes into a single TraceAttempt. */
  function combineStrokes(strokes: TraceAttempt[]): TraceAttempt | null {
    if (strokes.length === 0) return null;
    const allPoints = strokes.flatMap((s) => s.points);
    const xs = allPoints.map((p) => p.x);
    const ys = allPoints.map((p) => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const totalLength = strokes.reduce((sum, s) => sum + s.lengthPx, 0);
    // Wall-clock duration = last point time minus first point time across
    // strokes (including the gaps between them).
    const firstT = allPoints[0]?.t ?? 0;
    const lastT = allPoints[allPoints.length - 1]?.t ?? firstT;
    return {
      points: allPoints,
      boundingBox: { x: minX, y: minY, w: maxX - minX, h: maxY - minY },
      lengthPx: totalLength,
      durationMs: lastT - firstT,
    };
  }

  /** Invoked by the idle timer OR the manual "Done" button. */
  function commitAttempt() {
    if (idleTimer.current) {
      clearTimeout(idleTimer.current);
      idleTimer.current = null;
    }
    const strokes = strokeBuffer.current;
    strokeBuffer.current = [];
    setHasStrokes(false);

    const combined = combineStrokes(strokes);
    if (!combined) return;
    handleAttempt(combined);
  }

  function handleAttempt(attempt: TraceAttempt) {
    const c = canvasRef.current!;
    const rect = c.getBoundingClientRect();
    const score = scoreTrace(attempt, { width: rect.width, height: rect.height });
    setAttempts((a) => a + 1);

    if (score === 'ok') {
      setHint('أَحْسَنْتَ!');
      try {
        navigator.vibrate?.(25);
      } catch {
        /* unsupported */
      }
      setCompleted((n) => {
        const next = n + 1;
        if (next >= TARGET_TRACES) setTimeout(() => finish(next), 600);
        else
          setTimeout(() => {
            traceRef.current?.clear();
            setHint(null);
          }, 700);
        return next;
      });
      return;
    }

    if (score === 'too-short') {
      setHint('اِرْسُمِ الحَرْفَ كَامِلًا');
    } else {
      setHint('لَا تَسْتَعْجِلْ!');
    }
    setTimeout(() => {
      traceRef.current?.clear();
      setHint(null);
    }, 1000);
  }

  async function finish(completedCount: number) {
    const totalAttempts = Math.max(attempts, completedCount);
    const stars =
      completedCount >= TARGET_TRACES && totalAttempts <= 3
        ? 3
        : completedCount >= TARGET_TRACES && totalAttempts <= 5
          ? 2
          : completedCount >= TARGET_TRACES && totalAttempts <= 8
            ? 1
            : 0;

    if (currentUser.value) {
      try {
        await api('/api/progress', {
          method: 'POST',
          body: {
            letterId,
            stage: 2,
            stars,
            attempts: totalAttempts,
            errors: [],
          },
        });
        void refreshMap();
      } catch {
        // non-fatal
      }
    }
    try {
      navigator.vibrate?.([30, 40, 80]);
    } catch {
      /* unsupported */
    }
    setFinalStars(stars);
    setTimeout(() => route(`/island/${letterId}`), 1600);
  }

  function replayIntro() {
    setPhase('intro');
    setIntroKey((k) => k + 1);
    traceRef.current?.clear();
    strokeBuffer.current = [];
    setHasStrokes(false);
    if (idleTimer.current) {
      clearTimeout(idleTimer.current);
      idleTimer.current = null;
    }
  }

  function manualClear() {
    traceRef.current?.clear();
    strokeBuffer.current = [];
    setHasStrokes(false);
    if (idleTimer.current) {
      clearTimeout(idleTimer.current);
      idleTimer.current = null;
    }
  }

  if (!letter) {
    return (
      <div class="flex-1 grid place-items-center">
        <Spinner />
      </div>
    );
  }

  if (finalStars !== null) {
    return <Celebration stars={finalStars} scoreText={`${completed}/${TARGET_TRACES}`} />;
  }

  return (
    <div class="flex-1 flex flex-col items-center justify-between px-4 py-6 pb-6 max-w-md mx-auto w-full">
      <header class="w-full flex items-center justify-between">
        <NavButton ar="← إِنْهَاءٌ" en="Exit" onClick={() => route(`/island/${letterId}`)} />
        <div class="flex gap-1">
          {Array.from({ length: TARGET_TRACES }).map((_, i) => (
            <span
              key={i}
              class={`h-3 w-3 rounded-full ${i < completed ? 'bg-leaf' : 'bg-ink/15'}`}
            />
          ))}
        </div>
      </header>

      <div class="text-center">
        <p class="text-sm font-body font-bold text-ink/70">
          {phase === 'intro'
            ? 'شَاهِدْ أَوَّلًا…'
            : hasStrokes
              ? 'أَكْمِلْ أَوِ اِضْغَطْ أَتْمَمْتُ'
              : 'اِرْسُمِ الحَرْفَ بِأُصْبُعِكَ'}
        </p>
        <p class="text-[10px] text-ink/40" dir="ltr">
          {phase === 'intro'
            ? 'Watch first…'
            : hasStrokes
              ? 'Add dots if needed, then tap Done'
              : 'Trace the letter with your finger'}
        </p>
      </div>

      <div class="relative w-full aspect-square max-w-[85vw] rounded-3xl bg-white shadow-inner overflow-hidden">
        {/* Static dotted guide sits in every phase. */}
        <div
          class={`absolute inset-0 grid place-items-center text-[55vw] leading-none font-display select-none pointer-events-none transition-colors ${
            phase === 'intro' ? 'text-ink/5' : 'text-ink/10'
          }`}
          aria-hidden="true"
        >
          {letter.letter}
        </div>

        {/* Intro: the letter "writes itself" via SVG stroke animation. */}
        {phase === 'intro' && (
          <IntroDraw key={introKey} letterChar={letter.letter} />
        )}

        {/* Drawing canvas overlay — only interactive during drawing phase. */}
        <canvas
          ref={canvasRef}
          class={`absolute inset-0 w-full h-full rounded-3xl ${
            phase === 'drawing'
              ? 'touch-none cursor-crosshair'
              : 'pointer-events-none opacity-0'
          }`}
          style={phase === 'drawing' ? { touchAction: 'none' } : undefined}
        />
        {hint && (
          <div class="absolute top-4 left-1/2 -translate-x-1/2 bg-white rounded-full px-4 py-2 shadow-md font-display text-lg animate-pop">
            {hint}
          </div>
        )}
      </div>

      <div class="flex gap-3 flex-wrap justify-center">
        <button
          type="button"
          onClick={replayIntro}
          class="min-h-[52px] px-4 py-2 rounded-2xl bg-ocean/15 text-ocean flex flex-col items-center"
        >
          <span class="font-body font-bold text-sm">شَاهِدْ</span>
          <span class="text-[10px] opacity-70" dir="ltr">Show me</span>
        </button>
        <button
          type="button"
          onClick={manualClear}
          disabled={phase !== 'drawing' || !hasStrokes}
          class="min-h-[52px] px-4 py-2 rounded-2xl bg-ink/10 text-ink flex flex-col items-center disabled:opacity-40"
        >
          <span class="font-body font-bold text-sm">مَسْحٌ</span>
          <span class="text-[10px] opacity-60" dir="ltr">Clear</span>
        </button>
        <button
          type="button"
          onClick={commitAttempt}
          disabled={phase !== 'drawing' || !hasStrokes}
          class="min-h-[52px] px-4 py-2 rounded-2xl bg-leaf text-white shadow-md flex flex-col items-center disabled:opacity-40"
        >
          <span class="font-body font-bold text-sm">أَتْمَمْتُ</span>
          <span class="text-[10px] opacity-80" dir="ltr">Done</span>
        </button>
      </div>
    </div>
  );
}

/**
 * SVG-only "the letter writes itself" animation.
 *
 * Uses the same Aref Ruqaa display font the rest of the app uses.
 * Trick: render the glyph twice — once as an outline being drawn via
 * stroke-dashoffset, once as a fill fading in behind it. No real stroke
 * path data required (we'd need that for Phase 2's accuracy scoring).
 */
function IntroDraw({ letterChar }: { letterChar: string }) {
  return (
    <svg
      class="absolute inset-0 w-full h-full"
      viewBox="0 0 200 200"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Outline being drawn */}
      <text
        x="100"
        y="155"
        fontSize="180"
        fontFamily="'Aref Ruqaa', serif"
        fontWeight="700"
        textAnchor="middle"
        fill="none"
        stroke="#FF7A59"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-dasharray="1200"
        stroke-dashoffset="1200"
        style={{ animation: 'huroof-draw-outline 2.2s ease-out forwards' }}
      >
        {letterChar}
      </text>
      {/* Fill catching up behind the outline */}
      <text
        x="100"
        y="155"
        fontSize="180"
        fontFamily="'Aref Ruqaa', serif"
        fontWeight="700"
        textAnchor="middle"
        fill="#FF7A59"
        fill-opacity="0"
        style={{ animation: 'huroof-fill-in 1.2s ease-in 1.2s forwards' }}
      >
        {letterChar}
      </text>
      {/* Tiny pencil bobbing along — a stand-in for a real stroke cursor. */}
      <circle
        cx="100"
        cy="100"
        r="7"
        fill="#FFD45E"
        stroke="#1F2933"
        stroke-width="1.5"
        style={{ animation: 'huroof-pencil-bob 2.2s ease-in-out 1' }}
      />
    </svg>
  );
}


